import { cn } from '@/lib/utils'
import LinkWithTransition from '@/v2/components/helpers/link-with-transition'
import ResponsiveImage from '@/v2/components/helpers/responsive-image'
import SectionHead from '@/v2/components/sections/landing-pages/section-head'
import SectionWrapper from '@/v2/components/wrappers/section-wrapper'
import { fadeIn, staggerContainer, zoomIn } from '@/v2/constants/motion'
import { shippingPartners } from '@/v2/constants/shipping-partners'
import { FaqQuestionType } from '@/v2/types'
import { domAnimation, LazyMotion, m } from 'framer-motion'
import { useState } from 'react'

interface FaqSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string
  description?: string
  questions: FaqQuestionType[]
}

const FaqSection: React.FC<FaqSectionProps> = props => {
  const { title = 'Our Shipping Partners', description, questions, className, ...rest } = props

  const [activeFaq, setActiveFaq] = useState<number | null>(0)

  const handleActiveFaq = (id: number) => {
    setActiveFaq(prevActiveFaq => (prevActiveFaq === id ? null : id))
  }

  return (
    <LazyMotion features={domAnimation}>
      <m.section
        variants={staggerContainer(0.5, 0)}
        initial='hidden'
        whileInView='show'
        viewport={{ once: true, amount: 0.25 }}
        className={cn('text-center landing-pages-wr section-container section-spacing  bg-secondary-first', className)}
      >
        <SectionWrapper {...rest}>
          <SectionHead title={title} description={description} />

          {/* partners images */}
          <m.div
            variants={zoomIn(0, 1)}
            className='my-[48px] flex items-center justify-center md:gap-[40px] gap-[20px] md:w-[80%] mx-auto'
          >
            {shippingPartners?.map(partner => (
              <ResponsiveImage
                key={partner.id}
                src={partner.image.src}
                alt={partner.image.alt}
                className='max-w-[270px] h-[146px]'
              />
            ))}
          </m.div>

          {/* questions & answers */}
          <div className='flex flex-col gap-[16px]'>
            {questions?.map((faq, index) => {
              const answerArray = faq.answer.split('|||')
              const partner = shippingPartners.find(partner => partner.id === faq.question.toLowerCase())

              return (
                <m.article
                  key={faq.question}
                  className={'px-[19px] py-[8px] lg:px-5 lg:py-4 rounded-[10px] bg-background'}
                  variants={fadeIn('up', 'tween', 0.5, 1)}
                >
                  <div
                    onClick={() => handleActiveFaq(index)}
                    className='flex items-center justify-start gap-[10px] cursor-pointer'
                  >
                    <div
                      className={`${
                        activeFaq === index ? 'rotate-0' : 'rotate-[180deg]'
                      } transition-transform duration-300 hidden md:block`}
                    >
                      <svg xmlns='http://www.w3.org/2000/svg' width='2em' height='2em' viewBox='0 0 24 24'>
                        <path
                          fill='currentColor'
                          d='m12 10.828l-4.95 4.95l-1.414-1.414L12 8l6.364 6.364l-1.414 1.414z'
                        />
                      </svg>
                    </div>

                    <div
                      className={cn(
                        '[&>h3]:text-secondary lg:[&>h3]:text-[20px] [&>h3]:text-[18px] [&>h3]:font-[600] lg:[&>h3]:leading-[24px]',
                        activeFaq === index ? '[&>h3]:font-extrabold' : ''
                      )}
                    >
                      <h3>{faq.question}</h3>
                    </div>
                  </div>

                  <div
                    className={cn(
                      `transition-all duration-500 ease-in-out overflow-hidden md:px-[40px] text-left`,
                      activeFaq === index ? 'md-mn:max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                    )}
                    style={{ transitionProperty: 'max-height, opacity' }}
                  >
                    <h3 className='sr-only'>{faq.question}</h3>
                    {answerArray?.map((an, index) => (
                      <p
                        key={an}
                        className={cn(
                          'font-normal leading-[24px] text-paragraph mt-[16px]',
                          answerArray?.length > 2 && index === 1 ? 'font-bold' : ''
                        )}
                      >
                        {an}
                      </p>
                    ))}

                    {/* Displaying partner links if available */}
                    {partner && (
                      <m.ul
                        variants={fadeIn('up', 'tween', 0, 1)}
                        className='mt-[16px] list-inside flex md-mn:items-center gap-[20px] md-mn:flex-row flex-col'
                      >
                        {partner.links.map(link => (
                          <li key={link.href} className='font-bold underline'>
                            <LinkWithTransition href={link.href} target='_blank' rel='noopener noreferrer'>
                              {link.title}
                            </LinkWithTransition>
                          </li>
                        ))}
                      </m.ul>
                    )}
                  </div>
                </m.article>
              )
            })}
          </div>
        </SectionWrapper>
      </m.section>
    </LazyMotion>
  )
}

export default FaqSection
